import { useEffect, useState } from "react";
import { X, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ISLANDS } from "../lib/constants";
import { useT } from "../lib/LangContext";
import { DIGEST_LANGUAGES } from "../lib/i18n";
import { usePublicStats } from "../lib/useStats";
import { markSubscribed } from "../lib/useIsSubscribed";
import { getSubscribeSource } from "../lib/referral";
import { Flag } from "./Flag";
import { DigestLaunchNotice } from "./DigestLaunchNotice";

// Same threshold as sidebar so the number only shows when it's flattering.
const SOCIAL_PROOF_FLOOR = 25;

// A2P 10DLC / Twilio compliance consent copy — rendered as separate nodes so
// the Terms and Privacy words can be real <a> links that open in a new tab.
// Keyed by the DIGEST language the visitor picked in this modal.
const CONSENT_COPY = {
  en: {
    lead:
      "I agree to receive automated WhatsApp messages from IslandsDaily (up to 1 message per day). Message and data rates may apply. Reply HELP for help or STOP to unsubscribe at any time. By subscribing, I agree to the ",
    terms: "Terms of Service",
    and: " and ",
    privacy: "Privacy Policy",
    end: ".",
  },
  nl: {
    lead:
      "Ik ga ermee akkoord geautomatiseerde WhatsApp-berichten van IslandsDaily te ontvangen (maximaal 1 bericht per dag). Er kunnen bericht- en datakosten van toepassing zijn. Antwoord HELP voor hulp of STOP om u op elk moment af te melden. Door mij te abonneren ga ik akkoord met de ",
    terms: "Servicevoorwaarden",
    and: " en het ",
    privacy: "Privacybeleid",
    end: ".",
  },
  es: {
    lead:
      "Acepto recibir mensajes automatizados de WhatsApp de IslandsDaily (hasta 1 mensaje por día). Pueden aplicarse cargos por mensajes y datos. Responda HELP para obtener ayuda o STOP para darse de baja en cualquier momento. Al suscribirme, acepto los ",
    terms: "Términos de Servicio",
    and: " y la ",
    privacy: "Política de Privacidad",
    end: ".",
  },
};

export const WhatsAppModal = ({ open, onClose, defaultIsland = "all" }) => {
  const { t, tIsland, lang: uiLang } = useT();
  const stats = usePublicStats();
  const subCount = stats.subscribers || 0;
  const showProof = subCount >= SOCIAL_PROOF_FLOOR;
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [selectedIslands, setSelectedIslands] = useState(
    defaultIsland === "all" ? ["all"] : [defaultIsland]
  );
  const [digestLang, setDigestLang] = useState(uiLang || "en");
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);

  // Sync island + digest language every time the modal opens so a visitor
  // who navigates /aruba → /curacao (or flips the UI language toggle)
  // sees the CURRENT context pre-selected instead of whatever was set
  // when this component first mounted. Only fires on the closed→open
  // transition so we don't clobber the user's in-modal choices.
  useEffect(() => {
    if (!open) return;
    setSelectedIslands(defaultIsland === "all" ? ["all"] : [defaultIsland]);
    setDigestLang(uiLang || "en");
    setConsent(false);
  }, [open, defaultIsland, uiLang]);

  if (!open) return null;

  const toggleIsland = (slug) => {
    setSelectedIslands((prev) => {
      if (slug === "all") return ["all"];
      const without = prev.filter((s) => s !== "all" && s !== slug);
      if (prev.includes(slug)) {
        return without.length ? without : ["all"];
      }
      return [...without, slug];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Attach referral attribution (from ?ref=<channel> earlier in the
      // session) so admin analytics can measure which channels convert.
      // Currently wired: `digest_forward` for the WhatsApp "forward to
      // a friend" nudge (see lib/referral.js).
      const subscribeSource = getSubscribeSource();
      const payload = {
        phone, name, islands: selectedIslands, lang: digestLang,
      };
      if (subscribeSource) payload.subscribe_source = subscribeSource;
      const res = await api.post("/subscribe", payload);
      toast.success(res.data.message || "Subscribed!");
      markSubscribed();
      setPhone("");
      setName("");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("subscription_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="whatsapp-modal-overlay"
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl relative shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="whatsapp-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[#6B7280] hover:text-[#1A1A1A]"
          data-testid="whatsapp-modal-close"
        >
          <X size={20} />
        </button>
        <div className="p-6 sm:p-8">
          <div className="mb-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1">
              {t("daily_digest")}
            </div>
            <div className="font-serif-display text-2xl text-[#1A1A1A]">
              {t("digest_title")}
            </div>
          </div>
          <p className="text-sm text-[#6B7280] leading-relaxed mb-5">
            {t("digest_desc")}
          </p>

          <DigestLaunchNotice
            className="mb-5"
            testid="whatsapp-modal-launch-notice"
          />

          {showProof && (
            <div
              className="mb-5 -mt-2 inline-flex items-center gap-1.5 text-xs text-[#0AADA0] bg-[#0AADA0]/10 rounded-full px-3 py-1.5"
              data-testid="whatsapp-modal-social-proof"
            >
              <Users size={12} />
              <span>
                {(t("join_n_islanders") || "Join {count} islanders getting the daily digest")
                  .replace("{count}", subCount.toLocaleString())}
              </span>
            </div>
          )}

          <div className="mb-5">
            <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
              {t("which_islands")}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {ISLANDS.map((i) => {
                const checked = selectedIslands.includes(i.slug);
                return (
                  <button
                    type="button"
                    key={i.slug}
                    onClick={() => toggleIsland(i.slug)}
                    data-testid={`whatsapp-island-${i.slug}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      checked
                        ? "bg-[#0AADA0]/10 text-[#1A1A1A] ring-2 ring-[#0AADA0]"
                        : "bg-[#FAFAFA] text-[#6B7280] hover:bg-[#F0F0F0]"
                    }`}
                  >
                    <Flag emoji={i.flag} size={18} />
                    <span>{tIsland(i.slug)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
              {t("digest_language")}
            </label>
            {/* 5 digest-language options as of 2026-02-23 (Papiamento +
               Papiamentu added). Kept at grid-cols-3 across all
               breakpoints — the SubscribeModal has a fixed ~440px
               width, so a 5-col row truncates names like "Nederlands"
               and "Papiamento". 3-col grid renders as 2 rows (3+2)
               with room for full names. */}
            <div className="grid grid-cols-3 gap-2">
              {DIGEST_LANGUAGES.map((l) => {
                const active = digestLang === l.code;
                // Papiamentu uses two flags (🇨🇼 + 🇧🇶) because it is
                // standardised on both Curaçao and Bonaire. Every other
                // entry has a single `flag`. Render whichever is present.
                const flagEmojis = l.flags || [l.flag];
                return (
                  <button
                    type="button"
                    key={l.code}
                    onClick={() => setDigestLang(l.code)}
                    data-testid={`whatsapp-lang-${l.code}`}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      active
                        ? "bg-[#0AADA0]/10 text-[#1A1A1A] ring-2 ring-[#0AADA0]"
                        : "bg-[#FAFAFA] text-[#6B7280] hover:bg-[#F0F0F0]"
                    }`}
                  >
                    <span className="inline-flex items-center gap-0.5 shrink-0">
                      {flagEmojis.map((f, idx) => (
                        <Flag key={idx} emoji={f} size={18} />
                      ))}
                    </span>
                    <span className="flex flex-col items-start leading-tight">
                      <span>{l.name}</span>
                      {l.subLabel && (
                        <span className="text-[10px] text-[#9CA3AF] tracking-wide">
                          {l.subLabel}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
                {t("name_optional")}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Carlos"
                className="w-full bg-[#FAFAFA] rounded-lg px-4 py-3 text-[#1A1A1A] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
                data-testid="whatsapp-name-input"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
                {t("whatsapp_number")}
              </label>
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+297 5XX XXXX"
                className="w-full bg-[#FAFAFA] rounded-lg px-4 py-3 text-[#1A1A1A] focus:bg-white focus:ring-2 focus:ring-[#0AADA0] focus:outline-none text-sm"
                data-testid="whatsapp-phone-input"
              />
            </div>
            {(() => {
              const c = CONSENT_COPY[digestLang] || CONSENT_COPY.en;
              return (
                <label
                  className="flex items-start gap-2 cursor-pointer select-none"
                  data-testid="whatsapp-consent-label"
                >
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#25D366] shrink-0"
                    data-testid="whatsapp-consent-checkbox"
                  />
                  <span className="text-[11px] leading-relaxed text-[#6B7280]">
                    {c.lead}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0AADA0] underline hover:text-[#1A1A1A]"
                      data-testid="whatsapp-consent-terms-link"
                    >
                      {c.terms}
                    </a>
                    {c.and}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0AADA0] underline hover:text-[#1A1A1A]"
                      data-testid="whatsapp-consent-privacy-link"
                    >
                      {c.privacy}
                    </a>
                    {c.end}
                  </span>
                </label>
              );
            })()}
            <button
              type="submit"
              disabled={submitting || !consent}
              className="w-full py-3 rounded-full bg-[#E63946] hover:bg-[#cf2f3c] text-white text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="whatsapp-subscribe-submit"
            >
              {submitting ? t("subscribing") : t("subscribe_free")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
